const DATABASE_NAME = 'casur-geostudio-master-source';
const DATABASE_VERSION = 1;
const STORE_NAME = 'remembered-source';
const CURRENT_KEY = 'current';

export interface MasterSourceFileHandle {
  readonly kind: 'file';
  readonly name: string;
  getFile(): Promise<File>;
  queryPermission?(descriptor: { mode: 'read' }): Promise<PermissionState>;
  requestPermission?(descriptor: { mode: 'read' }): Promise<PermissionState>;
}

interface MasterSourceFileType {
  readonly description: string;
  readonly accept: Readonly<Record<string, readonly string[]>>;
}

export interface MasterSourceBrowser extends Window {
  showOpenFilePicker?: (options?: {
    readonly multiple?: boolean;
    readonly excludeAcceptAllOption?: boolean;
    readonly types?: readonly MasterSourceFileType[];
  }) => Promise<MasterSourceFileHandle[]>;
}

export interface MasterSourceMetadata {
  readonly name: string;
  readonly size: number;
  readonly lastModified: number;
  readonly format: string;
  readonly lastLoadedAt: string;
  /** V1.9.0 E5: the GeoPackage table used as the Maestro, so it can be recovered without asking again. */
  readonly layer?: string;
}

export interface RememberedMasterSource {
  readonly key: typeof CURRENT_KEY;
  readonly schemaVersion: 1;
  readonly handle: MasterSourceFileHandle;
  readonly metadata: MasterSourceMetadata;
}

export type MasterSourceReadResult =
  | { readonly status: 'ready'; readonly file: File; readonly changed: boolean }
  | { readonly status: 'permission-required' | 'denied' | 'unavailable'; readonly error?: string };

export interface MasterSourceRepository {
  load(): Promise<RememberedMasterSource | null>;
  save(source: RememberedMasterSource): Promise<void>;
}

const masterSourceTypes: readonly MasterSourceFileType[] = [{
  description: 'Maestro CASUR vectorial',
  accept: {
    'application/geo+json': ['.geojson', '.json'],
    'application/vnd.google-earth.kml+xml': ['.kml'],
    'application/vnd.google-earth.kmz': ['.kmz'],
    'application/zip': ['.zip'],
    'application/geopackage+sqlite3': ['.gpkg'],
  },
}];

export const supportsMasterSourcePicker = (browser: MasterSourceBrowser): boolean => typeof browser.showOpenFilePicker === 'function';

export async function pickMasterSource(browser: MasterSourceBrowser): Promise<MasterSourceFileHandle | null> {
  if (!browser.showOpenFilePicker) return null;
  const handles = await browser.showOpenFilePicker({ multiple: false, excludeAcceptAllOption: true, types: masterSourceTypes });
  return handles[0] ?? null;
}

export const masterSourceFormat = (name: string): string => {
  const extension = name.match(/\.([^.]+)$/)?.[1]?.toLocaleUpperCase();
  if (extension === 'JSON' || extension === 'GEOJSON') return 'GeoJSON';
  if (extension === 'ZIP') return 'ZIP / Shapefile';
  return extension ?? 'Desconocido';
};

export const createMasterSourceMetadata = (file: File, lastLoadedAt = new Date().toISOString(), layer?: string): MasterSourceMetadata => ({
  name: file.name,
  size: file.size,
  lastModified: file.lastModified,
  format: masterSourceFormat(file.name),
  lastLoadedAt,
  ...(layer ? { layer } : {}),
});

export const createRememberedMasterSource = (handle: MasterSourceFileHandle, file: File, lastLoadedAt = new Date().toISOString(), layer?: string): RememberedMasterSource => ({
  key: CURRENT_KEY,
  schemaVersion: 1,
  handle,
  metadata: createMasterSourceMetadata(file, lastLoadedAt, layer),
});

/**
 * V1.9.0 E5 · what to do with the remembered Maestro when GeoStudio opens.
 * - 'autoload': load it now, without dialogs: the browser already grants access, the file is the one loaded last
 *   time (same name, size and date) and nothing else is open (`idle`: no Maestro, no layers, no project opened).
 * - 'offer': the same file, but automatic recovery is turned off: offer «Recuperar».
 * - 'permission': the browser asks again for access, which needs a click: offer «Recuperar».
 * - 'changed': the file was modified since the last load: inform and let the user load the new version or choose another.
 * - 'missing': the file is gone or access was denied: inform and let the user choose it again.
 * - 'none': nothing remembered, or something is already open (never replace it).
 */
export type MasterRestoreAction = 'autoload' | 'offer' | 'permission' | 'changed' | 'missing' | 'none';

export function masterRestoreAction(result: MasterSourceReadResult | null, context: { readonly enabled: boolean; readonly idle: boolean }): MasterRestoreAction {
  if (!result || !context.idle) return 'none';
  if (result.status === 'permission-required') return 'permission';
  if (result.status !== 'ready') return 'missing';
  if (result.changed) return 'changed';
  return context.enabled ? 'autoload' : 'offer';
}

const AUTOLOAD_KEY = 'casur-geostudio:master-autoload';
/** «Recuperar automáticamente al abrir GeoStudio» (on by default); a per-device convenience, never project data. */
export function loadMasterAutoloadPreference(storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): boolean {
  try { return storage?.getItem(AUTOLOAD_KEY) !== '0'; } catch { return true; }
}
export function saveMasterAutoloadPreference(enabled: boolean, storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage): void {
  try { storage?.setItem(AUTOLOAD_KEY, enabled ? '1' : '0'); } catch { /* private mode: the default applies */ }
}

export const masterSourceChanged = (metadata: MasterSourceMetadata, file: File): boolean => (
  metadata.name !== file.name || metadata.size !== file.size || metadata.lastModified !== file.lastModified
);

export async function readMasterSource(source: RememberedMasterSource, requestPermission = false): Promise<MasterSourceReadResult> {
  let permission: PermissionState = 'granted';
  try {
    if (source.handle.queryPermission) permission = await source.handle.queryPermission({ mode: 'read' });
    if (permission === 'prompt' && requestPermission) {
      permission = source.handle.requestPermission ? await source.handle.requestPermission({ mode: 'read' }) : 'denied';
    }
  } catch (error) {
    return { status: 'unavailable', error: error instanceof Error ? error.message : 'No fue posible consultar el permiso del archivo.' };
  }
  if (permission === 'prompt') return { status: 'permission-required' };
  if (permission !== 'granted') return { status: 'denied' };
  try {
    const file = await source.handle.getFile();
    return { status: 'ready', file, changed: masterSourceChanged(source.metadata, file) };
  } catch (error) {
    return { status: 'unavailable', error: error instanceof Error ? error.message : 'El archivo ya no está disponible.' };
  }
}

const validSource = (value: unknown): value is RememberedMasterSource => {
  if (!value || typeof value !== 'object') return false;
  const source = value as Partial<RememberedMasterSource>;
  const metadata = source.metadata as Partial<MasterSourceMetadata> | undefined;
  return source.schemaVersion === 1 && source.key === CURRENT_KEY && source.handle?.kind === 'file' && typeof source.handle.getFile === 'function'
    && typeof metadata?.name === 'string' && typeof metadata.size === 'number' && typeof metadata.lastModified === 'number'
    && typeof metadata.format === 'string' && typeof metadata.lastLoadedAt === 'string' && (metadata.layer === undefined || typeof metadata.layer === 'string');
};

const requestResult = <T>(request: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  request.addEventListener('success', () => resolve(request.result));
  request.addEventListener('error', () => reject(request.error ?? new Error('IndexedDB no pudo completar la operación.')));
});

const transactionDone = (transaction: IDBTransaction): Promise<void> => new Promise((resolve, reject) => {
  transaction.addEventListener('complete', () => resolve());
  transaction.addEventListener('abort', () => reject(transaction.error ?? new Error('La transacción local fue cancelada.')));
  transaction.addEventListener('error', () => reject(transaction.error ?? new Error('La transacción local falló.')));
});

export class IndexedDbMasterSourceRepository implements MasterSourceRepository {
  constructor(private readonly factory: IDBFactory | undefined = globalThis.indexedDB) {}

  private async database(): Promise<IDBDatabase> {
    if (!this.factory) throw new Error('Este navegador no ofrece IndexedDB para recordar la fuente del Maestro CASUR.');
    const request = this.factory.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener('upgradeneeded', () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
    });
    return requestResult(request);
  }

  async load(): Promise<RememberedMasterSource | null> {
    const database = await this.database();
    try {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const done = transactionDone(transaction);
      const value: unknown = await requestResult(transaction.objectStore(STORE_NAME).get(CURRENT_KEY));
      await done;
      if (value === undefined) return null;
      if (!validSource(value)) throw new Error('La fuente recordada del Maestro CASUR usa un formato incompatible o incompleto.');
      return value;
    } finally { database.close(); }
  }

  async save(source: RememberedMasterSource): Promise<void> {
    const database = await this.database();
    try {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put(source);
      await transactionDone(transaction);
    } finally { database.close(); }
  }
}

export class MemoryMasterSourceRepository implements MasterSourceRepository {
  private source: RememberedMasterSource | null = null;
  async load(): Promise<RememberedMasterSource | null> { return this.source; }
  async save(source: RememberedMasterSource): Promise<void> { this.source = source; }
}
